import React, { useEffect, useState } from "react";
import { UserX, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { shortDate } from "../lib/format";
import { useTranslation } from "react-i18next";

/**
 * Admin: members who objected to the changed terms, and ending their contract.
 *
 * "Give notice" is ordinary termination with four weeks' notice (terms § 13.3). The
 * member is told at once in the app and by e-mail; accepting the new terms before the
 * date withdraws it; on the date the account is erased by the daily sweep
 * (backend/legal.py run_due_terminations). Each press is confirmed — it ends someone's
 * account — and "Withdraw notice" undoes it until then.
 */
export default function LegalObjections({ pageKey }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () => api.legalObjections(pageKey)
    .then((r) => setRows(r.data.objections || []))
    .catch(() => setRows([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [pageKey]);

  if (!rows || rows.length === 0) return null;

  const act = async (row, give) => {
    if (!window.confirm(t(give ? "legal.noticeConfirm" : "legal.withdrawConfirm", { email: row.email }))) return;
    setBusy(row.user_id);
    try {
      if (give) await api.legalGiveNotice(pageKey, row.user_id);
      else await api.legalWithdrawNotice(pageKey, row.user_id);
      toast.success(t(give ? "legal.noticeGiven" : "legal.noticeWithdrawn"));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("dialogs.failed"));
    } finally { setBusy(null); }
  };

  return (
    <div className="rounded-xl border border-slate-200 p-3.5 text-sm space-y-2" data-testid={`legal-objections-${pageKey}`}>
      <div className="font-medium text-slate-800 flex items-center gap-2">
        <UserX size={15} className="text-rose-500" /> {t("legal.objectionsTitle", { count: rows.length })}
      </div>
      {rows.map((row) => (
        <div key={row.user_id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 py-1.5 border-t border-slate-100">
          <div className="min-w-0">
            <div className="truncate text-slate-800">{row.email}</div>
            <div className="text-xs text-slate-400">
              {t("legal.objectedOn", { date: shortDate(row.objected_at) })}
              {row.notice_effective_at && <> · <span className="text-rose-600">{t("legal.closesOn", { date: shortDate(row.notice_effective_at) })}</span></>}
            </div>
          </div>
          {row.notice_effective_at ? (
            <button className="btn-ghost text-xs" disabled={busy === row.user_id} onClick={() => act(row, false)}
                    data-testid={`withdraw-notice-${row.user_id}`}>
              {t("legal.withdrawNotice")}
            </button>
          ) : (
            <button className="btn-outline text-xs text-rose-600 border-rose-200 hover:bg-rose-50"
                    disabled={busy === row.user_id || !row.in_force} title={row.in_force ? "" : t("legal.notInForceYet")}
                    onClick={() => act(row, true)} data-testid={`give-notice-${row.user_id}`}>
              {busy === row.user_id ? <Loader2 size={13} className="animate-spin" /> : null}
              {t("legal.giveNotice")}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
